import React, { useEffect, useState } from 'react';
import { 
  Box, 
  List, 
  ListItem, 
  ListItemButton, 
  ListItemText, 
  Typography, 
  IconButton, 
  Tooltip,
  Divider,
  Paper,
  CircularProgress
} from '@mui/material';
import { 
  Delete as DeleteIcon,
  Chat as ChatIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getUserChats, deleteChat } from '../../services/chatService';
import { ChatListItem } from '../../types/chat';

const ChatList: React.FC = () => {
  const [chats, setChats] = useState<ChatListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const navigate = useNavigate();

  useEffect(() => {
    fetchChats();
  }, []);

  const fetchChats = async () => {
    try {
      setLoading(true);
      const data = await getUserChats();
      setChats(data);
      setError('');
    } catch (err: any) {
      console.error('Error fetching chats:', err);
      setError('Failed to load chats. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenChat = (chatId: string) => {
    navigate(`/streaming-chat/${chatId}`);
  };

  const handleDeleteChat = async (e: React.MouseEvent, chatId: string) => {
    e.stopPropagation();
    
    if (!window.confirm('Are you sure you want to delete this chat?')) {
      return;
    }
    
    try {
      await deleteChat(chatId);
      setChats(chats.filter(chat => chat.id !== chatId));
    } catch (err: any) {
      console.error('Error deleting chat:', err);
      alert('Failed to delete chat. Please try again.');
    }
  };
  


  // All chats use the same icon now
  const getChatIcon = () => {
    return <ChatIcon color="primary" />;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (chats.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          No chats found. Start a new chat to begin.
        </Typography>
      </Box>
    );
  }

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        borderRadius: 2, 
        backgroundColor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden'
      }}
    >
      <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
        {chats.map((chat) => (
          <React.Fragment key={chat.id}>
            <ListItem 
              disablePadding 
              secondaryAction={
                <Tooltip title="Delete Chat">
                  <IconButton 
                    edge="end" 
                    aria-label="delete" 
                    onClick={(e) => handleDeleteChat(e, chat.id)}
                    sx={{ color: 'error.main' }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              }
            >
              <ListItemButton onClick={() => handleOpenChat(chat.id)}>
                <Box sx={{ mr: 2 }}>
                  {getChatIcon()}
                </Box>
                <ListItemText 
                  primary={
                    <Typography variant="subtitle1" noWrap>
                      {chat.name}
                    </Typography>
                  }
                  secondary={
                    chat.lastMessage ? (
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {chat.lastMessage.role === 'user' ? 'You: ' : 'Assistant: '}
                        {chat.lastMessage.content}
                      </Typography>
                    ) : 'No messages yet'
                  }
                />
              </ListItemButton>
            </ListItem>
            <Divider component="li" />
          </React.Fragment>
        ))}
      </List>

    </Paper>
  );
};

export default ChatList;
